import React, { useState, useCallback, useEffect } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import {
  ConnectionParam,
  ConnectionConfig,
  ConnectionHeader,
  ConnectionDefault,
} from '@/types/playbook';
import ConnectionParamEditor from './ConnectionParamEditor';
import LuaCodeEditor from './LuaCodeEditor';

interface ConnectionEditorProps {
  connectionJSON: ConnectionConfig | null;
  onUpdateConnection: (connection: ConnectionConfig) => void;
}

const ConnectionEditorTab: React.FC<ConnectionEditorProps> = ({
  connectionJSON,
  onUpdateConnection,
}) => {
  console.log('ConnectionEditorTab received connectionJSON:', connectionJSON);
  
  const [config, setConfig] = useState<ConnectionConfig>(() => {
    console.log('Initializing ConnectionEditorTab config with connectionJSON:', connectionJSON);
    if (!connectionJSON) {
      console.log('No connectionJSON provided, using default config');
      return {
        params: [],
        default: {
          endpoint: "",
          method: "POST",
          headers: [],
          batchSize: undefined,
          transform: {
            lang: "lua",
            body: ""
          }
        }
      };
    }
    
    // Handle imported connection YAML structure
    const connectionData = connectionJSON as any;
    let actualConnection: any = null;

    if (connectionData.endpointSchema) {
      actualConnection = connectionData.endpointSchema;
    } else if (connectionData.connectionTemplate) {
      actualConnection = connectionData.connectionTemplate;
    } else {
      // Fallback if it's neither, assume it's the direct object (e.g., from old state)
      actualConnection = connectionData;
    }
    
    console.log('Processing imported connection:', actualConnection);
    
    // Transform imported params to match our ConnectionParam interface
    const transformedParams: ConnectionParam[] = (actualConnection.params || []).map((param: any) => ({
      name: param.name,
      exampleValue: param.exampleValue || '',
      defaultValue: param.defaultValue || '',
      isOptional: param.isOptional || false,
      isPipelineVariable: param.isPipelineVariable || false,
      description: param.description || '',
      regexValidation: param.regexValidation || ''
    }));
    
    console.log('Transformed params:', transformedParams);
    
    // Transform headers from imported structure
    const transformedHeaders: ConnectionHeader[] = (actualConnection.default?.headers || []).map((header: any) => ({
      key: header.key,
      value: header.value
    }));
    
    return {
      params: transformedParams,
      default: {
        endpoint: actualConnection.default?.endpoint || "https://api.example.com/endpoint",
        method: actualConnection.default?.method || "POST",
        headers: transformedHeaders,
        batchSize: actualConnection.default?.batchSize || 10,
        transform: actualConnection.default?.transform ? {
          lang: actualConnection.default.transform.lang || 'lua',
          body: actualConnection.default.transform.body || ''
        } : {
          lang: "lua",
          body: "request.body = { events = batch, api_key = '$$API_KEY$$' }\nreturn request"
        }
      },
      eventSpecific: actualConnection.eventSpecific
    };
  });

  const [editingHeader, setEditingHeader] = useState<number | null>(null);

  // Update config when connectionJSON prop changes
  useEffect(() => {
    if (connectionJSON) {
      console.log('ConnectionEditorTab: connectionJSON prop changed, updating config');
      const connectionData = connectionJSON as any;
      let actualConnection: any = null;

      if (connectionData.endpointSchema) {
        actualConnection = connectionData.endpointSchema;
      } else if (connectionData.connectionTemplate) {
        actualConnection = connectionData.connectionTemplate;
      } else {
        actualConnection = connectionData;
      }
      
      // Transform imported params to match our ConnectionParam interface
      const transformedParams: ConnectionParam[] = (actualConnection.params || []).map((param: any) => ({
        name: param.name,
        exampleValue: param.exampleValue || '',
        defaultValue: param.defaultValue || '',
        isOptional: param.isOptional || false,
        isPipelineVariable: param.isPipelineVariable || false,
        description: param.description || '',
        regexValidation: param.regexValidation || ''
      }));
      
      // Transform headers from imported structure
      const transformedHeaders: ConnectionHeader[] = (actualConnection.default?.headers || []).map((header: any) => ({
        key: header.key,
        value: header.value
      }));
      
      const newConfig = {
        params: transformedParams,
        default: {
          endpoint: actualConnection.default?.endpoint || "https://api.example.com/endpoint",
          method: actualConnection.default?.method || "POST",
          headers: transformedHeaders,
          batchSize: actualConnection.default?.batchSize || 10,
          transform: actualConnection.default?.transform ? {
            lang: actualConnection.default.transform.lang || 'lua',
            body: actualConnection.default.transform.body || ''
          } : {
            lang: "lua",
            body: "request.body = { events = batch, api_key = '$$API_KEY$$' }\nreturn request"
          }
        },
        eventSpecific: actualConnection.eventSpecific
      };
      
      setConfig(newConfig);
    }
  }, [connectionJSON]);

  const updateConfig = useCallback((updates: Partial<ConnectionConfig>) => {
    const newConfig = { ...config, ...updates };
    setConfig(newConfig);
    onUpdateConnection(newConfig);
  }, [config, onUpdateConnection]);

  // Parameter management
  const addParam = useCallback(() => {
    const newParams = [...config.params, {
      name: "NEW_PARAM",
      exampleValue: "example-value",
      defaultValue: "",
      isOptional: false,
      isPipelineVariable: false // Default to false
    }];
    updateConfig({ params: newParams });
  }, [config.params, updateConfig]);

  const updateParam = useCallback((index: number, updates: Partial<ConnectionParam>) => {
    const newParams = config.params.map((param, i) => 
      i === index ? { ...param, ...updates } : param
    );
    updateConfig({ params: newParams });
  }, [config.params, updateConfig]);

  const deleteParam = useCallback((index: number) => {
    const newParams = config.params.filter((_, i) => i !== index);
    updateConfig({ params: newParams });
  }, [config.params, updateConfig]);

  // Header management
  const addHeader = useCallback(() => {
    const newHeaders = [...config.default.headers, { key: "New-Header", value: "header-value" }];
    updateConfig({ 
      default: { ...config.default, headers: newHeaders }
    });
    setEditingHeader(newHeaders.length - 1);
  }, [config.default, updateConfig]);

  const updateHeader = useCallback((index: number, updates: Partial<ConnectionHeader>) => {
    const newHeaders = config.default.headers.map((header, i) => 
      i === index ? { ...header, ...updates } : header
    );
    updateConfig({ 
      default: { ...config.default, headers: newHeaders }
    });
  }, [config.default, updateConfig]);

  const deleteHeader = useCallback((index: number) => {
    const newHeaders = config.default.headers.filter((_, i) => i !== index);
    updateConfig({ 
      default: { ...config.default, headers: newHeaders }
    });
  }, [config.default, updateConfig]);

  // Default configuration updates
  const updateDefault = useCallback((updates: Partial<ConnectionDefault>) => {
    updateConfig({ 
      default: { ...config.default, ...updates }
    });
  }, [config.default, updateConfig]);

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-9xl mx-auto p-6 space-y-6">
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Connection Configuration
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Configure the connection parameters, authentication, and default settings for your integration.
          </p>
        </div>

        {/* Parameters Section */}
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-md font-medium text-gray-900 dark:text-white">Parameters</h4>
          <button
            onClick={addParam}
            className="flex items-center px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Parameter
          </button>
        </div>
        
        <div className="space-y-3">
          {config.params.map((param, index) => (
            <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-md">
              <div className="flex-1">
                <ConnectionParamEditor
                  param={param}
                  onUpdate={(updates) => updateParam(index, updates)}
                />
              </div>
              <button
                onClick={() => deleteParam(index)}
                className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                title="Delete Parameter"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Default Configuration Section */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
        <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">Default Configuration</h4>
        
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              HTTP Method
            </label>
            <select
              value={config.default.method}
              onChange={(e) => updateDefault({ method: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            >
              <option value="GET">GET</option>
              <option value="POST">POST</option>
              <option value="PUT">PUT</option>
              <option value="PATCH">PATCH</option>
              <option value="DELETE">DELETE</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Batch Size
            </label>
            <input
              type="number"
              value={config.default.batchSize || ''}
              onChange={(e) => updateDefault({ batchSize: parseInt(e.target.value) || undefined })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              placeholder="10"
            />
          </div>
        </div>
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Endpoint URL
          </label>
          <input
            type="text"
            value={config.default.endpoint}
            onChange={(e) => updateDefault({ endpoint: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white font-mono"
            placeholder="https://api.example.com/endpoint"
          />
        </div>

        {/* Headers */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-3">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Headers
            </label>
            <button
              onClick={addHeader}
              className="flex items-center px-2 py-1 text-xs bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
            >
              <Plus className="h-3 w-3 mr-1" />
              Add Header
            </button>
          </div>
          
          <div className="space-y-2">
            {config.default.headers.map((header, index) => (
              <div key={index} className="grid grid-cols-12 gap-2 p-2 bg-gray-50 dark:bg-gray-700 rounded">
                <div className="col-span-5">
                  {editingHeader === index ? (
                    <input
                      type="text"
                      value={header.key}
                      onChange={(e) => updateHeader(index, { key: e.target.value })}
                      className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white font-mono"
                      onBlur={() => setEditingHeader(null)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') setEditingHeader(null);
                        if (e.key === 'Escape') setEditingHeader(null);
                      }}
                      autoFocus
                    />
                  ) : (
                    <div 
                      className="text-sm font-mono text-gray-900 dark:text-white cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 px-2 py-1 rounded"
                      onClick={() => setEditingHeader(index)}
                    >
                      {header.key}
                    </div>
                  )}
                </div>
                <div className="col-span-6">
                  <input
                    type="text"
                    value={header.value}
                    onChange={(e) => updateHeader(index, { value: e.target.value })}
                    className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white font-mono"
                  />
                </div>
                <div className="col-span-1 flex items-center">
                  <button
                    onClick={() => deleteHeader(index)}
                    className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                    title="Delete Header"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Transform Script */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Transform Script
          </label>
          <div className="grid grid-cols-4 gap-2 mb-2">
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Language
              </label>
              <select
                value={config.default.transform?.lang || 'lua'}
                onChange={(e) => updateDefault({ 
                  transform: { 
                    ...config.default.transform, 
                    lang: e.target.value,
                    body: config.default.transform?.body || ''
                  }
                })}
                className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              >
                <option value="lua">Lua</option>
                <option value="javascript">JavaScript</option>
                <option value="python">Python</option>
              </select>
            </div>
          </div>
          <LuaCodeEditor
            value={config.default.transform?.body || ''}
            onChange={(value) => updateDefault({ 
              transform: { 
                ...config.default.transform, 
                lang: config.default.transform?.lang || 'lua',
                body: value
              }
            })}
            placeholder="request.body = { events = batch, api_key = '$$API_KEY$$' }\nreturn request"
            rows={6}
            showLineNumbers={true}
          />
        </div>
      </div>

        {/* Raw JSON View (Collapsible) */}
        <details className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
          <summary className="p-4 cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
            View Generated YAML/JSON
          </summary>
          <div className="p-4 border-t border-gray-200 dark:border-gray-700">
            <pre className="text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-900 p-3 rounded-md text-xs overflow-auto max-h-64">
              {JSON.stringify(config, null, 2)}
            </pre>
          </div>
        </details>
      </div>
    </div>
  );
};

export default ConnectionEditorTab;
